import { useState, useCallback, useMemo } from "react";

export function usePersistentFilter(tableId, resetPaging = null, defaultSettings = null) {

  const defaultValues = useMemo(() => readTableSettings(tableId, defaultSettings), [tableId, defaultSettings]);

  const [filterOverride, _setFilterOverride] = useState(defaultValues.filterOverride);
  const [sortModel, _setSortModel] = useState(defaultValues.sortModel);
  const [visibleColumns, _setVisibleColumns] = useState(defaultValues.visibleColumns);
  const [pinnedColumns, _setPinnedColumns] = useState(defaultValues.pinnedColumns);
  const [bufferedTableId, setBufferedTableId] = useState(tableId);

  const isNotDefaultSort = useMemo(() => {
    if (!sortModel) return false;

    if (defaultSettings?.sortModel?.length > 0) {
      const defaultSort = defaultSettings.sortModel;

      if (sortModel.length !== defaultSort.length) {
        return true;
      }

      for (let i = 0; i < defaultSort.length; i++) {
        const a = defaultSort[i];
        const b = sortModel[i];

        if (a.field !== b.field || a.sort !== b.sort) {
          return true;
        }
      }

      return false;

    } else {
      return sortModel.length !== 0;
    }
  }, [sortModel, defaultSettings]);


  const isPinned = !!tableId && (
    isNotDefaultSort ||
    (!!filterOverride && filterOverride?.items?.length !== 0) ||
    pinnedColumns?.left?.length ||
    pinnedColumns?.right?.length
  );

  useMemo(() => {
    if (tableId !== bufferedTableId) {
      _setFilterOverride(defaultValues.filterOverride);
      _setSortModel(defaultValues.sortModel);
      _setVisibleColumns(defaultValues.visibleColumns);
      setBufferedTableId(tableId);
    }
  }, [defaultValues, tableId, bufferedTableId]);

  const isCurrent = !tableId || tableId === bufferedTableId;

  const setPinnedColumns = useCallback((data) => {
    _setPinnedColumns(data);
    writeTableSettings(tableId, visibleColumns, filterOverride, sortModel, data);
  }, [tableId, visibleColumns, filterOverride, sortModel]);

  const setSortModel = useCallback((data) => {
    if (!!resetPaging) {
      resetPaging();
    }
    _setSortModel(data);
    writeTableSettings(tableId, visibleColumns, filterOverride, data, pinnedColumns);
  }, [tableId, visibleColumns, filterOverride, pinnedColumns, resetPaging]);

  const setFilterOverride = useCallback((data) => {
    if (!!resetPaging) {
      resetPaging();
    }
    _setFilterOverride(data);
    writeTableSettings(tableId, visibleColumns, data, sortModel, pinnedColumns);
  }, [tableId, visibleColumns, sortModel, pinnedColumns, resetPaging]);

  const setVisibleColumns = useCallback((data) => {
    _setVisibleColumns(data);
    writeTableSettings(tableId, data, filterOverride, sortModel, pinnedColumns);
  }, [tableId, filterOverride, sortModel, pinnedColumns]);

  const onReset = useCallback(() => {
    if (!!resetPaging) {
      resetPaging();
    }

    _setFilterOverride({ items: [] });
    _setSortModel(defaultSettings?.sortModel || []);
    _setPinnedColumns({ left: [], right: [] });
    writeTableSettings(tableId, visibleColumns || {}, null, defaultSettings?.sortModel || []);
  }, [tableId, _setFilterOverride, _setSortModel, _setVisibleColumns, _setPinnedColumns, resetPaging, defaultSettings, visibleColumns]);

  return {
    sortModel: isCurrent ? sortModel : defaultValues.sortModel,
    filterOverride: isCurrent ? filterOverride : defaultValues.filterOverride,
    visibleColumns: isCurrent ? visibleColumns : defaultValues.visibleColumns,
    pinnedColumns: isCurrent ? pinnedColumns : defaultValues.pinnedColumns,
    isPinned,
    onReset,
    setSortModel,
    setFilterOverride,
    setVisibleColumns,
    setPinnedColumns,
    isCurrent
  };
}

const readTableSettings = (tableId, defaultSettings = null) => {
  if (!!tableId) {

    try {
      const json = localStorage.getItem(`tablesettings-v2-${tableId}`);
      const data = JSON.parse(json);

      return {
        visibleColumns: data?.visibleColumns || defaultSettings?.visibleColumns || {},
        filterOverride: data?.filterOverride || { items: [] },
        sortModel: data?.sortModel || defaultSettings?.sortModel,
        pinnedColumns: data?.pinnedColumns || { left: [], right: [] },
      }

    } catch (err) {
      console.log("cant read table settings", tableId, err);
    }
  }

  return {
    visibleColumns: defaultSettings?.visibleColumns || {},
    filterOverride: { items: [] },
    sortModel: [],
    pinnedColumns: { left: [], right: [] },
  };
}

const writeTableSettings = (tableId, visibleColumns = {}, filterOverride = null, sortModel = [], pinnedColumns = { left: [], right: [] }) => {
  if (!!tableId) {
    localStorage.setItem(`tablesettings-v2-${tableId}`, JSON.stringify({ visibleColumns, filterOverride, sortModel, pinnedColumns }));
  }
};

